import React, {useEffect, useState, useCallback} from 'react';

import Button from '@/components/ui/Button';
import axios, {AxiosResponse} from 'axios';
import AwesomeDebouncePromise from 'awesome-debounce-promise';
import {useStoreActions, useStoreState} from 'easy-peasy';
import {EVENT_TYPE_CALL, EVENT_TYPE_CHAT, EVENT_TYPE_EMAIL, EVENT_TYPE_FEEDBACK} from 'utils/eventConstants';
import {hasPermission} from '@/utils/permissions/permissionDebug';
import Col from 'react-bootstrap/Col';
import Row from 'react-bootstrap/Row';
import {navigate} from '@reach/router';
import Form from 'react-bootstrap/Form';
import LoadingWrapper from '@/components/loading/LoadingWrapper';
import HelpTextBlock from '@/components/help_text/HelpTextBlock';
import useOptimizedConfig from '@/hooks/useOptimizedConfig';
import TableComponent from '@/components/table/Table';
import ClickableTableCell from '@/components/table/ClickableTableCell';
import PaginationComponent from '@/components/pagination/PaginationComponent';
import i18n from 'i18n';
import {useFormattedDateTime} from '@/components/utils/FormattedDateTime';
import {useDateChangeListener} from '@/utils/useDateDependentDataRefresh';
import {Tooltip} from 'components/tooltips';
import {buildEventDetailURL} from '@/utils/urlBuilder';
import {isFieldRestricted} from '@/utils/fieldRestrictions';
import {RestrictedFieldIndicator} from '@/components/utils/DataNotAvailable';

interface Event {
    id: number;
    datetime: string;
    channel: {
        name: string;
    };
    lang: string;
    eventType: {
        name: string;
    };
    eventFeedbackFirstQuestion?: string;
    eventChatFirstMessage?: string;
    eventEmailSubject?: string;
    eventCallChannels?: string;
    eventCallDuration?: string;
    eventCallTransStatus?: string;
    settings?: any;
}

interface UserData {
    permissions?: string[];
    restrictedFieldKeys?: string[];
}

interface EventsResponse {
    events: Event[];
    pagesList: number[];
}

interface EventsListViewProps {
    activeType?: string;
    activeObject?: string;
    activeObjectType?: string;
}

const eventsUpdateRequest = (url: string, paramsDict: Record<string, any>): Promise<AxiosResponse<EventsResponse>> =>
    axios.get(url, {params: paramsDict})
        .catch((error) => {
            if (error.response?.status === 400) {
                return error.response;
            }
            throw error;
        });

const eventsUpdateRequestDebounced = AwesomeDebouncePromise(eventsUpdateRequest, 350);

const setSearchQueryState = async (
    setSearchQuery: (value: string) => void,
    value: string,
    setPage: (page: number) => void
): Promise<void> => {
    setPage(1);
    setSearchQuery(value);
};

const setSearchQueryStateDebounced = AwesomeDebouncePromise(setSearchQueryState, 350);

const EventsListViewInner: React.FC<EventsListViewProps & {userData: UserData}> = React.memo((
    {
        activeType,
        activeObject,
        activeObjectType,
        userData,
    },
) => {
    const {formatDateTime, isLoaded: configLoaded} = useFormattedDateTime();

    const events = useStoreState<Event[]>((state: any) => state.events.items);
    const storeEvents = useStoreActions((actions: any) => actions.events.loadEvents);

    const [pagesList, setPagesList] = useState<number[]>([]);
    const [currentPage, setCurrentPage] = useState<number>(1);

    const loadingEvents = useStoreState((state: any) => state.events.loading);
    const setLoadingEvents = useStoreActions((actions: any) => actions.events.setLoading);

    const [searchQuery, setSearchQuery] = useState<string>('');
    const restrictedFieldKeys: string[] = userData?.restrictedFieldKeys || [];

    const fetchData = useCallback(async (): Promise<void> => {
        if (!window.URLS?.eventsResource) {
            return;
        }

        setLoadingEvents(true);

        const paramsDict = {
            page: currentPage,
            eventType: activeType,
            activeObject,
            activeObjectType,
            query: searchQuery,
        };

        try {
            const response = await eventsUpdateRequestDebounced(window.URLS.eventsResource, paramsDict);

            storeEvents(response.data.events);
            setPagesList(response.data.pagesList || []);
        } catch (error) {
            console.error('Failed to fetch events:', error);
        } finally {
            setLoadingEvents(false);
        }
    }, [
        currentPage,
        activeType,
        activeObject,
        activeObjectType,
        searchQuery,
        storeEvents,
        setLoadingEvents,
    ]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    // Listen for date changes and refresh data
    useDateChangeListener(fetchData);

    const inputChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
        setSearchQueryStateDebounced(setSearchQuery, event.target.value, setCurrentPage);
    };

    const pageChanged = (page: number): void => {
        setCurrentPage(page);
    };

    // Ensure each combination of filters starts from page 1
    useEffect(() => {
        setCurrentPage(1);
    }, [activeType, activeObject, activeObjectType]);

    let additionalColumns: Array<{
        accessorKey: string;
        header: string;
    }>;
    if (activeType === EVENT_TYPE_FEEDBACK) {
        additionalColumns = [{
            accessorKey: 'eventFeedbackFirstQuestion',
            header: i18n.t('th_first_question.text'),
        }];
    } else if (activeType === EVENT_TYPE_CHAT) {
        additionalColumns = [{
            accessorKey: 'eventChatFirstMessage',
            header: i18n.t('th_first_message.text'),
        }];
    } else if (activeType === EVENT_TYPE_EMAIL) {
        additionalColumns = [{
            accessorKey: 'eventEmailSubject',
            header: i18n.t('th_subject.text'),
        }];
    } else if (activeType === EVENT_TYPE_CALL) {
        additionalColumns = [{
            accessorKey: 'eventCallChannels',
            header: i18n.t('th_speakers.text'),
        }, {
            accessorKey: 'eventCallDuration',
            header: i18n.t('th_duration_sec.text'),
        }, {
            accessorKey: 'eventCallTransStatus',
            header: i18n.t('th_trans_status.text'),
        }];
    } else {
        additionalColumns = [{
            accessorKey: 'eventType.name',
            header: i18n.t('th_event_type.text'),
        }];
    }

    const detailUrlGenerator = (rowData: Event): string => {
        return buildEventDetailURL(rowData.id);
    };

    const columns = [
        {
            accessorKey: 'id',
            header: i18n.t('th_text_id_column.text'),
            cell: (cell: any) => (
                <ClickableTableCell cell={cell} urlGenerator={detailUrlGenerator} />
            ),
        },
        {
            accessorKey: 'datetime',
            header: i18n.t('th_date.text'),
            cell: (cell: any) => {
                const value = cell.getValue();
                if (!value || value === '-') {
                    return '-';
                }
                return configLoaded ? formatDateTime(value, false, {zone: 'utc'}) : '';
            },
        },
        {
            accessorKey: 'channel.name',
            header: i18n.t('th_channel.text'),
            cell: (cell: any) => cell.getValue() || '-',
        },
        {
            accessorKey: 'lang',
            header: i18n.t('th_language.text'),
            cell: (cell: any) => {
                return cell.getValue() || '-';
            },
        },
        ...additionalColumns.map((col) => ({
            ...col,
            cell: (cell: any) => {
                const value = cell.getValue();
                if (value) return value;
                if (isFieldRestricted(col.accessorKey, restrictedFieldKeys)) {
                    return <RestrictedFieldIndicator />;
                }
                return '-';
            },
        })),
        {
            accessorKey: 'settings',
            header: i18n.t('th_action.text'),
            cell: (cell: any) => (
                <div className="d-inline-block">
                    <Tooltip content={i18n.t('jt_view.text')} placement="top">
                        <Button
                            variant="outline-info"
                            size="sm"
                            icon="mdi-eye"
                            onClick={() => navigate(detailUrlGenerator(cell.row.original))}
                            data-cy="view-button"
                        />
                    </Tooltip>
                </div>
            ),
        },
    ];

    return (
        <>
            {
                window.SCONSTANTS.sessionAgent !== 'true' && activeObjectType === 'customer' ? (
                    <HelpTextBlock helpText={i18n.t('ht_feelingstream_customer.text')} />
                ) : null
            }

            {
                window.SCONSTANTS.sessionAgent !== 'true' && activeObjectType === 'agent' ? (
                    <HelpTextBlock helpText={i18n.t('ht_feelingstream_agent.text')} />
                ) : null
            }

            {
                window.SCONSTANTS.sessionAgent !== 'true' && activeObjectType === 'team' ? (
                    <HelpTextBlock helpText={i18n.t('ht_team_event.text')} />
                ) : null
            }

            {
                window.SCONSTANTS.sessionAgent !== 'true' && ([EVENT_TYPE_CHAT, EVENT_TYPE_CALL, EVENT_TYPE_EMAIL, EVENT_TYPE_FEEDBACK].indexOf(activeType || '') > -1) ? (
                    <HelpTextBlock helpText={i18n.t('ht_use_this_view_to_see_the_list_of_all_events_for_the_chosen_category.text')} />
                ) : null
            }

            {
                window.SCONSTANTS.sessionAgent !== 'true' && !([EVENT_TYPE_CHAT, EVENT_TYPE_CALL, EVENT_TYPE_EMAIL, EVENT_TYPE_FEEDBACK].indexOf(activeType || '') > -1)
                    && !(['customer', 'agent', 'team'].indexOf(activeObjectType || '') > -1) ? (
                    <HelpTextBlock helpText={i18n.t('ht_feelingstream_customer.text')} />
                ) : null
            }

            {
                window.SCONSTANTS.sessionAgent === 'true' ? (
                    <HelpTextBlock helpText={i18n.t('ht_this_view_customer_conversations.text')} />
                ) : null
            }

            <Row>
                <Col lg={3}>
                    <Form
                        className="mb-2 search-box"
                        onSubmit={(e: React.FormEvent) => {
                            e.preventDefault();
                        }}
                    >
                        <Form.Control
                            type="text"
                            placeholder={i18n.t('es_search_external.text')}
                            data-cy={`${activeType}-search`}
                            onChange={(event: React.ChangeEvent<HTMLInputElement>) => inputChange(event)}
                        />
                    </Form>
                </Col>
            </Row>

            <LoadingWrapper
                loading={loadingEvents}
                loadingText={i18n.t('jt_loading_data_general.text')}
                minHeight="400px"
            >
                <div className="card">
                    <div className="card-body">
                        <TableComponent data={events} columns={columns} />
                        <PaginationComponent
                            pages={pagesList}
                            currentPage={currentPage}
                            onPageChange={pageChanged}
                            hideWhenSinglePage={false}
                        />
                    </div>
                </div>
            </LoadingWrapper>

        </>
    );
});

const EventsListView: React.FC<EventsListViewProps> = React.memo((
    {
        activeType,
        activeObject,
        activeObjectType,
    },
) => {
    const {userData, loading: loadingUserData} = useOptimizedConfig();

    const userCanAccessEventsResource = hasPermission('fs.event.list', userData?.permissions, true) || false;

    if (!userCanAccessEventsResource && !loadingUserData) {
        return (
            <>
                {
                    window.SCONSTANTS.sessionAgent !== 'true' && activeObjectType === 'customer' ? (
                        <HelpTextBlock helpText={i18n.t('ht_feelingstream_customer.text')} />
                    ) : null
                }

                {
                    window.SCONSTANTS.sessionAgent !== 'true' && activeObjectType === 'agent' ? (
                        <HelpTextBlock helpText={i18n.t('ht_feelingstream_agent.text')} />
                    ) : null
                }

                {
                    window.SCONSTANTS.sessionAgent !== 'true' && activeObjectType === 'team' ? (
                        <HelpTextBlock helpText={i18n.t('ht_team_event.text')} />
                    ) : null
                }

                {
                    window.SCONSTANTS.sessionAgent !== 'true' && ([EVENT_TYPE_CHAT, EVENT_TYPE_CALL, EVENT_TYPE_EMAIL, EVENT_TYPE_FEEDBACK].indexOf(activeType || '') > -1) ? (
                        <HelpTextBlock helpText={i18n.t('ht_use_this_view_to_see_the_list_of_all_events_for_the_chosen_category.text')} />
                    ) : null
                }

                {
                    window.SCONSTANTS.sessionAgent !== 'true' && !([EVENT_TYPE_CHAT, EVENT_TYPE_CALL, EVENT_TYPE_EMAIL, EVENT_TYPE_FEEDBACK].indexOf(activeType || '') > -1)
                        && !(['customer', 'agent', 'team'].indexOf(activeObjectType || '') > -1) ? (
                        <HelpTextBlock helpText={i18n.t('ht_feelingstream_customer.text')} />
                    ) : null
                }

                {
                    window.SCONSTANTS.sessionAgent === 'true' ? (
                        <HelpTextBlock helpText={i18n.t('ht_this_view_customer_conversations.text')} />
                    ) : null
                }

                <div className="alert alert-danger" role="alert">
                    {i18n.t('tt_you_dont_have_permission_to_access_the_requested_resource.text')}
                </div>
            </>
        );
    }

    if (loadingUserData || !userData) {
        return null;
    }

    return (
        <EventsListViewInner
            activeType={activeType}
            activeObject={activeObject}
            activeObjectType={activeObjectType}
            userData={userData}
        />
    );
});

export default EventsListView;
